// pages/SocialPage.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CommentIcon from "@mui/icons-material/Comment";
import { storage, db, auth } from "../../src/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, orderBy } from "firebase/firestore";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const PostInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: "#262626",
  borderRadius: "10px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#262626" },
    "&:hover fieldset": { borderColor: "#262626" },
    "&.Mui-focused fieldset": { borderColor: "#262626" },
  },
  "& .MuiInputBase-input": { color: "white" },
}));

const PostCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: "#262626",
  borderRadius: "10px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
}));

const SocialPage = ({ onSubmit }) => {
  const [postContent, setPostContent] = useState("");
  const [posts, setPosts] = useState([]);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageURL, setImageURL] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        console.log("User not authenticated, cannot fetch posts in SocialPage");
        return;
      }

      const fetchPosts = () => {
        console.log("Fetching posts for user in SocialPage:", user.uid);

        const postsQuery = query(
          collection(db, "posts"),
          orderBy("timestamp", "desc")
        );
        

        const unsubscribe = onSnapshot(postsQuery, (snapshot) => {
          const postsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("SocialPage fetched posts:", postsData);
          setPosts(postsData);
        }, (error) => {
         
        });

        return unsubscribe;
      };

      const unsubscribePosts = fetchPosts();
      return () => unsubscribePosts && unsubscribePosts();
    });

    return () => unsubscribeAuth && unsubscribeAuth();
  }, []);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  async function handleImageChange(e) {
    const selectedImage = e.target.files[0];
    if (!selectedImage) return;

    setImage(selectedImage);
    try {
      setUploading(true);
      const uniqueFileName = `posters/${Date.now()}_${selectedImage.name}`;
      const storageRef = ref(storage, uniqueFileName);

      await uploadBytes(storageRef, selectedImage);
      const downloadURL = await getDownloadURL(storageRef);

      console.log("Uploaded image URL:", downloadURL);
      setImageURL(downloadURL);
    } catch (error) {
      console.error("Image Upload Error:", error.code, error.message);
      setSnackbarMessage("Failed to upload image. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setUploading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploading) {
      setSnackbarMessage("Please wait for the image to finish uploading");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    if (!postContent && !imageURL) {
      setSnackbarMessage("Please add some content or an image");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    if (!auth.currentUser) {
      console.error("No authenticated user found when submitting post");
      setSnackbarMessage("You must be logged in to post");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    console.log("Creating post with userId:", auth.currentUser.uid);

    try {
      const postData = {
        content: postContent,
        image: imageURL || null,
        likes: 0,
        comments: 0,
        timestamp: serverTimestamp(),
        userId: auth.currentUser.uid,
      };

      await addDoc(collection(db, "posts"), postData);

      setPostContent("");
      setImage(null);
      setImageURL("");

      setSnackbarMessage("Post created successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      if (onSubmit) onSubmit(postData);
    } catch (error) {
      console.error("Error posting:", error);
      setSnackbarMessage("Failed to create post. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    if (timestamp.toDate) return timestamp.toDate().toLocaleString();
    if (timestamp instanceof Date) return timestamp.toLocaleString();
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Container maxWidth="lg" sx={{ backgroundColor: "#1a1a1a", color: "white", paddingTop: 4 }}>
      <Box flex={3} mr={2}>
        <Typography variant="h4" gutterBottom>
          Social Feed
        </Typography>

        <form onSubmit={handleSubmit}>
          <PostInput
            variant="outlined"
            placeholder="What’s on your mind?"
            fullWidth
            multiline
            rows={2}
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            sx={{ mb: 2 }}
          />

          <input 
            type="file" 
            accept="image/*" 
            onChange={handleImageChange} 
            style={{ marginTop: "10px" }} 
          />
          {uploading && <Typography sx={{ color: "yellow" }}>Uploading...</Typography>}
          {imageURL && (
            <img 
              src={imageURL} 
              alt="Post Preview" 
              width="100" 
              height="100" 
              style={{ marginTop: "10px" }} 
            />
          )}

          <Button
            variant="contained"
            type="submit"
            sx={{
              backgroundColor: "#7d1aed",
              color: "white",
              borderRadius: "20px",
              marginLeft: 2,
              "&:hover": { backgroundColor: "#6a1ab8" },
            }}
          >
            Post
          </Button>
        </form>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%", backgroundColor: "#2d2d2d", color: "#ffffff" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default SocialPage;